import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import {
  getAvailableAccountsApiV1ExternalAccountsAvailableGet,
  selectAccountsApiV1ExternalAccountsSelectPost,
} from "@/backend-sdk/client/sdk.gen";
import type {
  AccountInfo,
  DiscoveredAccounts,
  DiscoveryErrorInfo,
  SelectAccountItem,
} from "@/backend-sdk/client/types.gen";
import { isHeyApiError } from "@/lib/hey-api-client";
import { CHAVE_DAS_CONTAS_SELECIONADAS } from "@/hooks/useEstadoDasFontes";
import type { AvailableAccountsResponse } from "@/backend-sdk/client/types.gen";
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Search,
  Target,
  Database,
  Instagram,
  Chrome,
  TrendingUp,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

const MAX_ATTEMPTS = 4;
const RETRY_DELAY_MS = 1000;

interface SubItem {
  property_id?: string; // GA4 property
  account_id?: string; // Google Ads subaccount
  name: string;
  currency?: string;
  timezone?: string;
}

interface AvailableAccount {
  type: string;
  external_id: string;
  name: string;
  currency?: string;
  timezone?: string;
  properties?: SubItem[]; // GA4 properties
  subaccounts?: SubItem[]; // Google Ads subaccounts
}

type DiscoveryErrorPayload = DiscoveryErrorInfo;
type AccountType = keyof AccountsByType;

interface AccountsByType {
  ga4?: AvailableAccount[];
  google_ads?: AvailableAccount[];
  search_console?: AvailableAccount[];
  meta_ads?: AvailableAccount[];
  instagram_insights?: AvailableAccount[];
}

const getErrorDescription = (error: unknown, fallback: string) => {
  if (isHeyApiError(error)) {
    if (typeof error.detail === "string") return error.detail;
    if (
      error.detail &&
      typeof error.detail === "object" &&
      "message" in error.detail
    ) {
      const message = (error.detail as { message?: unknown }).message;
      if (typeof message === "string") return message;
    }
  }
  return fallback;
};

const AccountTypeConfig = {
  ga4: {
    name: "Google Analytics 4",
    description: "Selecione uma conta e uma propriedade GA4",
    icon: TrendingUp,
    color: "text-blue-600",
    hasHierarchy: true,
  },
  google_ads: {
    name: "Google Ads",
    description: "Selecione uma conta e uma subconta (se houver)",
    icon: Search,
    color: "text-green-600",
    hasHierarchy: true,
  },
  search_console: {
    name: "Search Console",
    description: "Sites Search Console disponíveis",
    icon: Chrome,
    color: "text-yellow-600",
    hasHierarchy: false,
  },
  meta_ads: {
    name: "Meta Ads",
    description: "Contas de anúncios Meta disponíveis",
    icon: Target,
    color: "text-blue-600",
    hasHierarchy: false,
  },
  instagram_insights: {
    name: "Instagram",
    description: "Páginas Instagram Business disponíveis",
    icon: Instagram,
    color: "text-pink-600",
    hasHierarchy: false,
  },
} satisfies Record<
  AccountType,
  {
    name: string;
    description: string;
    icon: typeof TrendingUp;
    color: string;
    hasHierarchy: boolean;
  }
>;

const getAccountInfoExternalId = (account: AccountInfo): string | null => {
  return (
    account.account_id ?? account.property_id ?? account.website_url ?? null
  );
};

const getAccountInfoName = (account: AccountInfo): string => {
  return (
    account.account_name ??
    account.website_url ??
    account.property_id ??
    account.account_id ??
    "Sem nome"
  );
};

const toAvailableAccount = (
  account: AccountInfo,
  type: AccountType,
): AvailableAccount | null => {
  const externalId = getAccountInfoExternalId(account);
  if (!externalId) return null;

  const base: AvailableAccount = {
    type,
    external_id: externalId,
    name: getAccountInfoName(account),
  };

  if (type === "ga4" && account.property_id) {
    base.properties = [
      {
        property_id: account.property_id,
        name: getAccountInfoName(account),
      },
    ];
  }

  if (account.subaccounts && account.subaccounts.length > 0) {
    base.subaccounts = account.subaccounts
      .filter(
        (sub): sub is typeof sub & { account_id: string } => !!sub.account_id,
      )
      .map((sub) => ({
        account_id: sub.account_id,
        property_id: sub.property_id ?? undefined,
        name: sub.account_name ?? sub.account_id ?? "Sem nome",
      }));
  }

  return base;
};

const mapAccountInfos = (
  accounts: AccountInfo[] | null | undefined,
  type: AccountType,
): AvailableAccount[] | undefined => {
  const mapped = (accounts ?? [])
    .map((account) => toAvailableAccount(account, type))
    .filter((account): account is AvailableAccount => account !== null);
  return mapped.length > 0 ? mapped : undefined;
};

const mapDiscoveredAccounts = (
  discovered: DiscoveredAccounts | null | undefined,
): AccountsByType => ({
  ga4: mapAccountInfos(discovered?.ga4, "ga4"),
  google_ads: mapAccountInfos(discovered?.google_ads, "google_ads"),
  search_console: mapAccountInfos(discovered?.search_console, "search_console"),
  meta_ads: mapAccountInfos(discovered?.meta_ads, "meta_ads"),
  instagram_insights: mapAccountInfos(
    discovered?.instagram_insights,
    "instagram_insights",
  ),
});

const AccountSelectionPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const source = searchParams.get("source"); // 'google' ou 'meta'

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState<AccountsByType>({});
  const [discoveryErrors, setDiscoveryErrors] = useState<
    Record<string, DiscoveryErrorPayload>
  >({});
  const [selectedAccounts, setSelectedAccounts] = useState<{
    [key: string]: { accountId: string; subItemId?: string };
  }>({});
  const [expandedAccounts, setExpandedAccounts] = useState<{
    [key: string]: boolean;
  }>({});
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});

  const loadAvailableAccounts = async () => {
    try {
      setLoading(true);

      // Obter customer_uuid da subconta selecionada (se houver)
      const customerUuid = localStorage.getItem("active_customer_uuid");

      // A primeira tentativa costuma demorar (cache frio do backend), então
      // tentamos até MAX_ATTEMPTS vezes com 1s de intervalo antes de exibir
      // erro — sem toast nas tentativas intermediárias.
      let data: AvailableAccountsResponse | null = null;
      let lastError: unknown = new Error("Erro desconhecido");
      for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
        try {
          const { data: response } =
            await getAvailableAccountsApiV1ExternalAccountsAvailableGet({
              query: {
                source: source || "",
                customer_uuid: customerUuid || undefined,
              },
              throwOnError: true,
            });
          data = response;
          break;
        } catch (error) {
          lastError = error;
          if (attempt < MAX_ATTEMPTS) {
            await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
          }
        }
      }

      if (!data) {
        throw lastError;
      }

      const mappedAccounts = mapDiscoveredAccounts(data.accounts);
      setAccounts(mappedAccounts);
      setDiscoveryErrors(data.errors || {});

      // Se não houver nenhuma conta disponível, mostrar erro
      const totalAccounts = Object.values(mappedAccounts).reduce(
        (sum, arr) => sum + (arr?.length || 0),
        0,
      );

      const totalErrors = Object.keys(data.errors || {}).length;
      if (totalAccounts === 0 && totalErrors === 0) {
        toast({
          title: "Nenhuma conta encontrada",
          description:
            "Não foram encontradas contas disponíveis para esta integração",
          variant: "destructive",
        });
        navigate("/dashboard/integrations");
      }
    } catch (error) {
      console.error("Erro ao carregar contas:", error);
      toast({
        title: "Erro ao carregar contas",
        description: getErrorDescription(error, "Erro desconhecido"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!source || (source !== "google" && source !== "meta")) {
      toast({
        title: "Erro",
        description: "Fonte de integração inválida",
        variant: "destructive",
      });
      navigate("/dashboard/integrations");
      return;
    }

    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadAvailableAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  const toggleExpand = (accountType: string, accountId: string) => {
    const key = `${accountType}-${accountId}`;
    setExpandedAccounts((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleAccountSelection = (
    accountType: string,
    accountId: string,
    subItemId?: string,
  ) => {
    setSelectedAccounts((prev) => {
      const newSelection = { ...prev };
      const currentSelection = newSelection[accountType];

      // Se está selecionando o mesmo item, desselecionar
      if (
        currentSelection?.accountId === accountId &&
        currentSelection?.subItemId === subItemId
      ) {
        delete newSelection[accountType];
      } else {
        // Caso contrário, selecionar novo item
        newSelection[accountType] = { accountId, subItemId };
      }

      return newSelection;
    });
  };

  const handleSaveAndSync = async () => {
    try {
      setSaving(true);

      // Validar: deve ter pelo menos uma conta selecionada
      if (Object.keys(selectedAccounts).length === 0) {
        toast({
          title: "Selecione pelo menos uma conta",
          description:
            "Você precisa selecionar ao menos uma conta para continuar",
          variant: "destructive",
        });
        return;
      }

      // Montar lista de contas selecionadas
      const selectedAccountsList: SelectAccountItem[] = Object.entries(
        selectedAccounts,
      ).map(([accountType, selection]) => {
        const typedAccountType = accountType as AccountType;
        const accountsList = accounts[typedAccountType];
        const account = accountsList?.find(
          (acc) => acc.external_id === selection.accountId,
        );

        console.log("[DEBUG] Processando seleção:", {
          accountType,
          selectionAccountId: selection.accountId,
          selectionSubItemId: selection.subItemId,
          accountFound: !!account,
          accountExternalId: account?.external_id,
        });

        // IMPORTANTE: external_id deve ser o ID da conta no banco (account_id), NÃO property_id ou site_url
        // O backend vai buscar pela tabela external_accounts usando esse ID
        let externalId = selection.accountId;
        let name = account?.name || "";
        let propertyId: string | undefined;

        if (selection.subItemId) {
          if (
            (accountType === "ga4" || accountType === "google_ads") &&
            account?.subaccounts
          ) {
            const subaccount = account.subaccounts.find(
              (s) => s.account_id === selection.subItemId,
            );
            if (subaccount) {
              externalId = subaccount.account_id!;
              name = subaccount.name;
              propertyId = subaccount.property_id;
            }
          }
        }

        const payload: SelectAccountItem = {
          type: accountType,
          external_id: externalId,
          name,
          currency: account?.currency,
          timezone: account?.timezone,
        };

        if (accountType === "ga4") {
          payload.metadata = { selected_property_id: propertyId ?? externalId };
        }

        console.log("[DEBUG] Payload gerado:", payload);
        return payload;
      });

      console.log(
        "[DEBUG] Lista completa enviada ao backend:",
        selectedAccountsList,
      );

      // Obter customer_uuid da subconta selecionada (se houver)
      const customerUuid = localStorage.getItem("active_customer_uuid");

      // Salvar contas selecionadas
      const { data: response } =
        await selectAccountsApiV1ExternalAccountsSelectPost({
          body: {
            source: source || "",
            selected_accounts: selectedAccountsList,
            customer_uuid: customerUuid || undefined,
          },
          throwOnError: true,
        });

      // O prefixo cobre as consultas sobre a seleção: a lista de onde os N/D tiram o "falta
      // integrar" e a conta do Instagram que a tela do Instagram manda às rotas. Sem invalidar,
      // as duas seguiriam com a seleção anterior até a consulta vencer.
      void queryClient.invalidateQueries({
        queryKey: [CHAVE_DAS_CONTAS_SELECIONADAS],
      });

      toast({
        title: "Contas salvas com sucesso!",
        description: `${response.accounts_saved} conta(s) salva(s).`,
      });

      // Redirecionar para a página de integrações (sem parâmetro integration=success)
      navigate("/dashboard/integrations?source=" + source);
    } catch (error) {
      console.error("Erro ao salvar contas:", error);
      toast({
        title: "Erro ao salvar contas",
        description: getErrorDescription(error, "Erro desconhecido"),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const renderAccountTypeSection = (accountType: AccountType) => {
    const accountsList = accounts[accountType];
    const discoveryError = discoveryErrors?.[accountType as string];
    const hasCachedAccounts = Boolean(
      discoveryError?.cached_accounts_used && (accountsList?.length || 0) > 0,
    );
    const isRateLimited = discoveryError?.status_code === 429;

    if ((!accountsList || accountsList.length === 0) && !discoveryError) {
      return null;
    }

    const config = AccountTypeConfig[accountType];
    const Icon = config.icon;
    const hasHierarchy = config.hasHierarchy;

    const sectionSearch = searchTerms[accountType] || "";
    const filteredAccounts = (
      (sectionSearch
        ? (accountsList || []).filter(
            (account) =>
              account.name
                ?.toLowerCase()
                .includes(sectionSearch.toLowerCase()) ||
              account.external_id
                ?.toLowerCase()
                .includes(sectionSearch.toLowerCase()) ||
              account.subaccounts?.some((sub) =>
                sub.name?.toLowerCase().includes(sectionSearch.toLowerCase()),
              ) ||
              account.properties?.some((prop) =>
                prop.name?.toLowerCase().includes(sectionSearch.toLowerCase()),
              ),
          )
        : accountsList) || []
    ).toSorted((a, b) => (a.name || "").localeCompare(b.name || ""));

    return (
      <Card key={accountType} className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <Icon className={`h-6 w-6 ${config.color}`} />
          <div>
            <h3 className="text-lg font-semibold">{config.name}</h3>
            <p className="text-sm text-muted-foreground">
              {config.description}
            </p>
          </div>
        </div>

        {discoveryError ? (
          <Alert
            variant={
              hasCachedAccounts || isRateLimited ? "default" : "destructive"
            }
            className="mb-4"
          >
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>
              {hasCachedAccounts
                ? "Limite temporário da API"
                : isRateLimited
                  ? "Google Ads temporariamente limitado"
                  : "Não foi possível conectar"}
            </AlertTitle>
            <AlertDescription>
              <div className="space-y-2">
                {isRateLimited ? (
                  <div className="text-sm">
                    A API do Google Ads atingiu o limite temporário de
                    requisições. Aguarde alguns minutos e tente novamente.
                  </div>
                ) : (
                  <div>
                    {discoveryError?.status_code
                      ? `HTTP ${discoveryError.status_code}`
                      : "Erro ao conectar"}
                  </div>
                )}
                {discoveryError?.message && (
                  <div className="text-sm">{discoveryError.message}</div>
                )}
                {hasCachedAccounts && (
                  <div className="text-sm text-muted-foreground">
                    Exibindo{" "}
                    {discoveryError.cached_accounts_count ||
                      accountsList?.length ||
                      0}{" "}
                    conta(s) em cache local. Voce pode continuar a selecao
                    normalmente enquanto a API externa se recupera.
                  </div>
                )}
                <div className="text-xs text-muted-foreground">
                  Tipo: {discoveryError.type}
                </div>
              </div>
            </AlertDescription>
          </Alert>
        ) : (
          <Alert className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Atenção</AlertTitle>
            <AlertDescription>
              {hasHierarchy
                ? "Selecione uma conta e depois selecione UMA propriedade/subconta."
                : "Selecione APENAS UMA conta."}
            </AlertDescription>
          </Alert>
        )}

        <div className="relative mb-3">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar conta..."
            value={sectionSearch}
            onChange={(e) =>
              setSearchTerms((prev) => ({
                ...prev,
                [accountType]: e.target.value,
              }))
            }
            className="pl-9 h-9"
          />
        </div>

        <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
          {(!filteredAccounts || filteredAccounts.length === 0) &&
            discoveryError && (
              <div className="text-sm text-muted-foreground">
                Nenhuma conta foi carregada para esta fonte devido ao erro
                acima.
              </div>
            )}
          {filteredAccounts &&
            filteredAccounts.length === 0 &&
            !discoveryError && (
              <div className="text-sm text-muted-foreground py-4 text-center">
                Nenhuma conta encontrada para "{sectionSearch}"
              </div>
            )}
          {(filteredAccounts || []).map((account) => {
            const accountKey = `${accountType}-${account.external_id}`;
            const isExpanded = expandedAccounts[accountKey];
            const selection = selectedAccounts[accountType];
            const isAccountSelected =
              selection?.accountId === account.external_id;

            // Verificar se a conta tem sub-items (deve vir antes de usar!)
            const hasSubItems =
              (account.properties && account.properties.length > 0) ||
              (account.subaccounts && account.subaccounts.length > 0);

            // Conta principal selecionada (sem sub-item)
            const isParentSelected = isAccountSelected && !selection?.subItemId;

            // Sub-item selecionado na conta principal
            const hasSubItemSelected =
              isAccountSelected && !!selection?.subItemId;

            return (
              <div key={account.external_id} className="border rounded-lg">
                {/* Account Header */}
                <div
                  className={`flex items-center space-x-3 p-3 cursor-pointer transition-colors ${
                    isParentSelected
                      ? "border-primary bg-primary/5"
                      : hasSubItemSelected
                        ? "bg-accent/50"
                        : "hover:bg-accent"
                  }`}
                  onClick={() => {
                    if (hasHierarchy && hasSubItems) {
                      toggleExpand(accountType, account.external_id);
                    } else {
                      handleAccountSelection(accountType, account.external_id);
                    }
                  }}
                >
                  {hasHierarchy && hasSubItems && (
                    <div
                      className="flex-shrink-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleExpand(accountType, account.external_id);
                      }}
                    >
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      )}
                    </div>
                  )}

                  <Checkbox
                    id={`${accountType}-${account.external_id}`}
                    checked={isParentSelected}
                    onClick={(e) => e.stopPropagation()}
                    onCheckedChange={() =>
                      handleAccountSelection(accountType, account.external_id)
                    }
                  />

                  <div
                    className="flex-1 cursor-pointer"
                    onClick={(e) => {
                      if (hasHierarchy && hasSubItems) {
                        e.stopPropagation();
                        toggleExpand(accountType, account.external_id);
                      }
                    }}
                  >
                    <div>
                      <div className="font-medium">
                        {account.name || account.external_id || "Sem nome"}
                      </div>
                      {account.name && account.name !== account.external_id && (
                        <div className="text-sm text-muted-foreground">
                          ID: {account.external_id}
                        </div>
                      )}
                      {(account.currency || account.timezone) && (
                        <div className="text-sm text-muted-foreground">
                          {account.currency && `${account.currency}`}
                          {account.currency && account.timezone && " • "}
                          {account.timezone && `${account.timezone}`}
                        </div>
                      )}
                    </div>
                  </div>

                  {isParentSelected && (
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                  )}
                </div>

                {/* Sub-items (Properties or Subaccounts) */}
                {hasHierarchy && hasSubItems && isExpanded && (
                  <div className="border-t bg-muted/30">
                    <div className="p-2 space-y-1 max-h-[200px] overflow-y-auto">
                      {(() => {
                        const visibleProperties = (account.properties || [])
                          .filter(
                            (prop) =>
                              !sectionSearch ||
                              prop.name
                                ?.toLowerCase()
                                .includes(sectionSearch.toLowerCase()),
                          )
                          .toSorted((a, b) =>
                            (a.name || "").localeCompare(b.name || ""),
                          );
                        const visibleSubaccounts = (account.subaccounts || [])
                          .filter(
                            (sub) =>
                              !sectionSearch ||
                              sub.name
                                ?.toLowerCase()
                                .includes(sectionSearch.toLowerCase()),
                          )
                          .toSorted((a, b) =>
                            (a.name || "").localeCompare(b.name || ""),
                          );

                        return (
                          <>
                            {visibleProperties.map((property) => {
                              const isSelected =
                                selection?.accountId === account.external_id &&
                                selection?.subItemId === property.property_id;

                              return (
                                <div
                                  key={property.property_id}
                                  className={`flex items-center space-x-3 p-2 pl-8 rounded cursor-pointer transition-colors ${
                                    isSelected
                                      ? "border border-primary bg-primary/10"
                                      : "hover:bg-accent"
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAccountSelection(
                                      accountType,
                                      account.external_id,
                                      property.property_id,
                                    );
                                  }}
                                >
                                  <Checkbox
                                    id={`${accountType}-${account.external_id}-${property.property_id}`}
                                    checked={isSelected}
                                    onClick={(e) => e.stopPropagation()}
                                    onCheckedChange={() =>
                                      handleAccountSelection(
                                        accountType,
                                        account.external_id,
                                        property.property_id,
                                      )
                                    }
                                  />
                                  <div className="flex-1 cursor-pointer text-sm">
                                    {property.name}
                                  </div>
                                  {isSelected && (
                                    <CheckCircle2 className="h-4 w-4 text-primary" />
                                  )}
                                </div>
                              );
                            })}

                            {visibleSubaccounts.map((subaccount) => {
                              const isSelected =
                                selection?.accountId === account.external_id &&
                                selection?.subItemId === subaccount.account_id;

                              return (
                                <div
                                  key={subaccount.account_id}
                                  className={`flex items-center space-x-3 p-2 pl-8 rounded cursor-pointer transition-colors ${
                                    isSelected
                                      ? "border border-primary bg-primary/10"
                                      : "hover:bg-accent"
                                  }`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleAccountSelection(
                                      accountType,
                                      account.external_id,
                                      subaccount.account_id,
                                    );
                                  }}
                                >
                                  <Checkbox
                                    id={`${accountType}-${account.external_id}-${subaccount.account_id}`}
                                    checked={isSelected}
                                    onClick={(e) => e.stopPropagation()}
                                    onCheckedChange={() =>
                                      handleAccountSelection(
                                        accountType,
                                        account.external_id,
                                        subaccount.account_id,
                                      )
                                    }
                                  />
                                  <div className="flex-1 cursor-pointer text-sm">
                                    {subaccount.name}
                                  </div>
                                  {isSelected && (
                                    <CheckCircle2 className="h-4 w-4 text-primary" />
                                  )}
                                </div>
                              );
                            })}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center space-y-4">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">
            Carregando contas disponíveis...
          </p>
        </div>
      </div>
    );
  }

  const sourceName = source === "google" ? "Google" : "Meta";
  const totalSelected = Object.keys(selectedAccounts).length;

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
      <header>
        <h1 className="text-3xl font-bold tracking-tight">
          Seleção de Contas - {sourceName}
        </h1>
        <p className="text-muted-foreground mt-2">
          Selecione as contas e propriedades desejadas. Apenas uma seleção por
          tipo.
        </p>
      </header>

      <Alert>
        <Database className="h-4 w-4" />
        <AlertTitle>Como funciona?</AlertTitle>
        <AlertDescription>
          Para Google Analytics e Google Ads, expanda a conta para selecionar
          uma propriedade ou subconta específica. Após salvar, suas contas
          estarão prontas para uso.
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        {source === "google" && (
          <>
            {renderAccountTypeSection("ga4")}
            {renderAccountTypeSection("google_ads")}
            {renderAccountTypeSection("search_console")}
          </>
        )}

        {source === "meta" && (
          <>
            {renderAccountTypeSection("meta_ads")}
            {renderAccountTypeSection("instagram_insights")}
          </>
        )}
      </div>

      <div className="flex justify-between items-center pt-4 border-t">
        <div className="text-sm text-muted-foreground">
          {totalSelected === 0
            ? "Nenhuma conta selecionada"
            : `${totalSelected} conta${totalSelected > 1 ? "s" : ""} selecionada${totalSelected > 1 ? "s" : ""}`}
        </div>

        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={() => navigate("/dashboard/integrations")}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSaveAndSync}
            disabled={saving || totalSelected === 0}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Salvar Contas
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AccountSelectionPage;
